import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Heart, ArrowRight, Bed, Bath, Square } from 'lucide-react';

interface PropertyCardProps {
  property: any;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, isFavorite, onToggleFavorite }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // --- MAPPING EXACT SUPABASE ---
  const id = property?.id;
  const title = property?.titre || "Sans titre";
  const type = property?.type || "Location";
  const price = property?.prix || 0;
  const zone = property?.zone || "Zone non spécifiée";
  const photos = Array.isArray(property?.photos) ? property.photos : [];
  
  // Caractéristiques pour les icônes
  const chambres = property?.chambres || 0;
  const douches = property?.douches || 0;
  const surface = property?.surface || 0;

  // Image par défaut si le tableau photos est vide
  const displayImage = photos.length > 0 
    ? photos[currentImageIndex] 
    : "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80";

  // Animation du carrousel au survol
  useEffect(() => {
    let interval: any;
    if (isHovered && photos.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % photos.length);
      }, 1200);
    } else {
      setCurrentImageIndex(0);
    }
    return () => clearInterval(interval);
  }, [isHovered, photos.length]);

  const formatPrice = (p: number, t: string) => {
    const formatted = new Intl.NumberFormat('fr-BJ').format(p);
    // On n'affiche "/mois" que si ce n'est pas une vente
    if (t === 'Vente-Achat') {
      return `${formatted} FCFA`;
    }
    return `${formatted} FCFA/mois`;
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) onToggleFavorite(id);
  };

  return (
    <div 
      className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col h-[420px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/bien/${id}`} className="block h-[65%] relative overflow-hidden">
        {/* Image */}
        <div className="w-full h-full relative bg-gray-200">
          <img
            src={displayImage}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={(e) => { (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
          
          {/* Badge Type */}
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 rounded-lg text-xs font-bold shadow-lg text-white backdrop-blur-md
                ${type === 'Location' ? 'bg-blue-600/90' : type === 'Meublé' ? 'bg-purple-600/90' : 'bg-orange/90'}`}>
                {type === 'Vente-Achat' ? 'À VENDRE' : type.toUpperCase()}
            </span>
          </div>

          {/* Favoris */}
          <button
            onClick={handleFavoriteClick}
            className={`absolute top-3 right-3 p-2 rounded-full shadow-lg transition-all ${
              isFavorite ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-400 hover:text-red-500'
            }`}
          >
            <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
          </button>
        </div>
      </Link>

      {/* Contenu de la carte */}
      <div className="p-4 flex flex-col justify-between flex-grow">
        <div>
          <div className="flex justify-between items-start mb-1">
            <h3 className="text-lg font-bold text-navy truncate flex-1" title={title}>{title}</h3>
            <span className="text-orange font-bold whitespace-nowrap ml-2">
              {formatPrice(price, type)}
            </span>
          </div>
          
          <div className="flex items-center text-gray-500 text-sm mb-3">
            <MapPin size={14} className="mr-1 text-orange" />
            <span className="truncate">{zone}</span>
          </div>

          {/* Caractéristiques */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100 text-gray-600">
            <div className="flex items-center gap-1">
              <Bed size={16} className="text-navy" />
              <span className="text-xs font-medium">{chambres} ch.</span>
            </div>
            <div className="flex items-center gap-1">
              <Bath size={16} className="text-navy" />
              <span className="text-xs font-medium">{douches} d.</span>
            </div>
            <div className="flex items-center gap-1">
              <Square size={16} className="text-navy" />
              <span className="text-xs font-medium">{surface} m²</span>
            </div>
          </div>
        </div>

        <Link 
          to={`/bien/${id}`} 
          className="mt-2 flex items-center justify-center w-full py-2 bg-gray-50 hover:bg-navy hover:text-white text-navy font-bold rounded-xl transition-colors text-sm"
        >
          Voir les détails
          <ArrowRight size={16} className="ml-2" />
        </Link>
      </div>
    </div>
  );
};

export default PropertyCard;
